/**
 * Type-safe DOM manipulation utilities
 * Provides null-safe, typed wrappers around common DOM operations
 */

/**
 * Type-safe querySelector that returns a single element or null
 * @template T - The expected element type
 * @param selector - CSS selector string
 * @param parent - Optional parent element or document (defaults to document)
 * @returns The found element or null
 *
 * @example
 * const button = querySelector<HTMLButtonElement>('.my-button');
 * if (button) button.disabled = true;
 */
export function querySelector<T extends Element>(
  selector: string,
  parent: Element | Document = document
): T | null {
  try {
    return parent.querySelector<T>(selector);
  } catch (error) {
    console.error(`querySelector failed for selector "${selector}":`, error);
    return null;
  }
}

/**
 * Type-safe querySelectorAll that returns an array of elements
 * @template T - The expected element type
 * @param selector - CSS selector string
 * @param parent - Optional parent element or document (defaults to document)
 * @returns Array of found elements (empty array if none found)
 *
 * @example
 * const buttons = querySelectorAll<HTMLButtonElement>('.my-buttons');
 * buttons.forEach(btn => btn.disabled = true);
 */
export function querySelectorAll<T extends Element>(
  selector: string,
  parent: Element | Document = document
): T[] {
  try {
    return Array.from(parent.querySelectorAll<T>(selector));
  } catch (error) {
    console.error(`querySelectorAll failed for selector "${selector}":`, error);
    return [];
  }
}

/**
 * Type-safe element creation with optional attributes
 * @template K - The HTML element tag name
 * @param tag - HTML tag name
 * @param attributes - Optional record of attribute name-value pairs
 * @param textContent - Optional text content for the element
 * @returns The created element
 *
 * @example
 * const div = createElement('div', { class: 'container', id: 'main' });
 * const button = createElement('button', { type: 'button' }, 'Click me');
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes?: Record<string, string>,
  textContent?: string
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);

  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
  }

  if (textContent) {
    element.textContent = textContent;
  }

  return element;
}

/**
 * Type-safe event listener with proper typing
 * @template K - The event type key
 * @param element - The element to attach the listener to
 * @param event - The event name
 * @param handler - The event handler function
 * @param options - Optional event listener options
 *
 * @example
 * const button = querySelector<HTMLButtonElement>('.my-button');
 * if (button) {
 *   addEventListener(button, 'click', (e) => {
 *     console.log('Button clicked', e.target);
 *   });
 * }
 */
export function addEventListener<K extends keyof HTMLElementEventMap>(
  element: Element | null,
  event: K,
  handler: (e: HTMLElementEventMap[K]) => void,
  options?: AddEventListenerOptions
): void {
  if (!element) {
    console.warn(`Cannot add event listener "${event}": element is null`);
    return;
  }

  element.addEventListener(event, handler as EventListener, options);
}

/**
 * Remove an event listener from an element
 * @template K - The event type key
 * @param element - The element to remove the listener from
 * @param event - The event name
 * @param handler - The event handler function to remove
 * @param options - Optional event listener options
 */
export function removeEventListener<K extends keyof HTMLElementEventMap>(
  element: Element | null,
  event: K,
  handler: (e: HTMLElementEventMap[K]) => void,
  options?: EventListenerOptions
): void {
  if (!element) {
    console.warn(`Cannot remove event listener "${event}": element is null`);
    return;
  }

  element.removeEventListener(event, handler as EventListener, options);
}

/**
 * Add or remove a class from an element
 * @param element - The element to modify
 * @param className - The class name to toggle
 * @param force - Optional boolean to force add (true) or remove (false)
 * @returns True if class is present after toggle, false otherwise
 *
 * @example
 * toggleClass(element, 'active'); // Toggle
 * toggleClass(element, 'active', true); // Force add
 * toggleClass(element, 'active', false); // Force remove
 */
export function toggleClass(
  element: Element | null,
  className: string,
  force?: boolean
): boolean {
  if (!element) {
    console.warn(`Cannot toggle class "${className}": element is null`);
    return false;
  }

  return element.classList.toggle(className, force);
}

/**
 * Add one or more classes to an element
 * @param element - The element to modify
 * @param classNames - Class names to add
 *
 * @example
 * addClass(element, 'active', 'visible');
 */
export function addClass(element: Element | null, ...classNames: string[]): void {
  if (!element) {
    console.warn(`Cannot add classes: element is null`);
    return;
  }

  element.classList.add(...classNames);
}

/**
 * Remove one or more classes from an element
 * @param element - The element to modify
 * @param classNames - Class names to remove
 *
 * @example
 * removeClass(element, 'active', 'visible');
 */
export function removeClass(element: Element | null, ...classNames: string[]): void {
  if (!element) {
    console.warn(`Cannot remove classes: element is null`);
    return;
  }

  element.classList.remove(...classNames);
}

/**
 * Check if an element has a specific class
 * @param element - The element to check
 * @param className - The class name to check for
 * @returns True if element has the class, false otherwise
 */
export function hasClass(element: Element | null, className: string): boolean {
  if (!element) return false;
  return element.classList.contains(className);
}

/**
 * Set an attribute on an element
 * @param element - The element to modify
 * @param name - The attribute name
 * @param value - The attribute value
 *
 * @example
 * setAttribute(button, 'disabled', 'true');
 * setAttribute(input, 'data-value', '123');
 */
export function setAttribute(
  element: Element | null,
  name: string,
  value: string
): void {
  if (!element) {
    console.warn(`Cannot set attribute "${name}": element is null`);
    return;
  }

  element.setAttribute(name, value);
}

/**
 * Get an attribute from an element
 * @param element - The element to read from
 * @param name - The attribute name
 * @returns The attribute value or null if not found
 */
export function getAttribute(element: Element | null, name: string): string | null {
  if (!element) return null;
  return element.getAttribute(name);
}

/**
 * Remove an attribute from an element
 * @param element - The element to modify
 * @param name - The attribute name
 */
export function removeAttribute(element: Element | null, name: string): void {
  if (!element) {
    console.warn(`Cannot remove attribute "${name}": element is null`);
    return;
  }

  element.removeAttribute(name);
}

/**
 * Set the text content of an element
 * @param element - The element to modify
 * @param text - The text content
 */
export function setText(element: Element | null, text: string): void {
  if (!element) {
    console.warn('Cannot set text: element is null');
    return;
  }

  element.textContent = text;
}

/**
 * Set the HTML content of an element
 * @param element - The element to modify
 * @param html - The HTML content
 */
export function setHTML(element: Element | null, html: string): void {
  if (!element) {
    console.warn('Cannot set HTML: element is null');
    return;
  }

  element.innerHTML = html;
}

/**
 * Get the value of an input element
 * @param element - The input element
 * @returns The input value or empty string if element is null
 */
export function getValue(
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null
): string {
  if (!element) return '';
  return element.value;
}

/**
 * Set the value of an input element
 * @param element - The input element
 * @param value - The value to set
 */
export function setValue(
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null,
  value: string
): void {
  if (!element) {
    console.warn('Cannot set value: element is null');
    return;
  }

  element.value = value;
}

/**
 * Show an element by removing the 'hidden' attribute and display: none
 * @param element - The element to show
 */
export function show(element: HTMLElement | null): void {
  if (!element) return;
  element.style.display = '';
  element.removeAttribute('hidden');
}

/**
 * Hide an element by setting display: none
 * @param element - The element to hide
 */
export function hide(element: HTMLElement | null): void {
  if (!element) return;
  element.style.display = 'none';
}

/**
 * Check if an element is visible
 * @param element - The element to check
 * @returns True if element is visible, false otherwise
 */
export function isVisible(element: HTMLElement | null): boolean {
  if (!element) return false;
  return element.style.display !== 'none' && !element.hasAttribute('hidden');
}

/**
 * Append a child element to a parent
 * @param parent - The parent element
 * @param child - The child element to append
 */
export function appendChild(
  parent: Element | null,
  child: Element | null
): void {
  if (!parent || !child) {
    console.warn('Cannot append child: parent or child is null');
    return;
  }

  parent.appendChild(child);
}

/**
 * Remove an element from the DOM
 * @param element - The element to remove
 */
export function removeElement(element: Element | null): void {
  if (!element) return;
  element.remove();
}

/**
 * Clear all children from an element
 * @param element - The element to clear
 */
export function clearChildren(element: Element | null): void {
  if (!element) return;
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}
